import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Spinner } from "flowbite-react";

const DEFAULT_TIPS = [
  "Conferindo parâmetros e credenciais",
  "Buscando registros nas bases nacionais",
  "Consolidando indicadores relevantes",
  "Ajustando o painel com os dados recebidos",
];

const ACCENT = {
  brand: { ring: "ring-sky-400/60", dot: "bg-sky-500" },
  indigo: { ring: "ring-indigo-400/60", dot: "bg-indigo-500" },
  emerald: { ring: "ring-emerald-400/60", dot: "bg-emerald-500" },
  slate: { ring: "ring-slate-400/60", dot: "bg-slate-600" },
};

export default function ConsultaLoading({
  title = "Processando sua consulta...",
  subtitle = "Aguarde enquanto cruzamos fontes confiáveis.",
  tips = DEFAULT_TIPS,
  fullscreen = false,
  accent = "brand",
  className = "",
  cardSize = "md",
}) {
  const [tipIndex, setTipIndex] = useState(0);

  let logoSrc;

  switch (true) {
    case window.location.href.includes("avaliecreditoconsultas"):
      logoSrc = "/assets/white-label/avalie-credito/logo.png";
      break;
    case window.location.href.includes("acessopositivo"):
      logoSrc = "/assets/white-label/acesso-positivo/logo-azul.png";
      break;
    case window.location.href.includes("proconsultbr"):
      logoSrc = "/assets/white-label/pro-consult-br/logo_consult.png";
      break;
    case window.location.href.includes("simscore"):
      logoSrc = "/assets/white-label/sim-pesquisa/logo_sim.png";
      break;
    case window.location.href.includes("simpesquisa"):
      logoSrc = "/assets/white-label/sim-pesquisa/logo_sim.png";
      break;
    case window.location.href.includes("credoresbrasil"):
      logoSrc = "/assets/white-label/credores-brasil/logo.png";
      break;
    case window.location.href.includes("consultaspositivo"):
      logoSrc = "/assets/white-label/consultas-positivo/logo.png";
      break;
    case window.location.href.includes("raioxconsultas"):
      logoSrc = "/assets/white-label/raio-x/logo.png";
      break;
    case window.location.href.includes("obahcredito"):
      logoSrc = "/assets/white-label/obah-credito/logo.png";
      break;
    case window.location.href.includes("hellusconsultoria"):
      logoSrc = "/assets/white-label/hellus-consultoria/logo.png";
      break;
    case window.location.href.includes("nomenoazul"):
      logoSrc = "/assets/white-label/nome-no-azul/logo.png";
      break;
    case window.location.href.includes("finposcore"):
      logoSrc = "/assets/white-label/finpo-score/logo.png";
      break;
    case window.location.href.includes("periciadecredito"):
      logoSrc = "/assets/white-label/pericia-de-credito/logo.png";
      break;
    default:
      logoSrc = "/assets/relinq_1.png";
      break;
  }

  useEffect(() => {
    if (!tips.length) return;
    const id = setInterval(
      () => setTipIndex((current) => (current + 1) % tips.length),
      4800,
    );
    return () => clearInterval(id);
  }, [tips]);

  const palette = ACCENT[accent] ?? ACCENT.brand;

  const sizeCls = useMemo(() => {
    const mapping = {
      sm: "max-w-xl p-4 md:p-6",
      md: "max-w-2xl p-4 md:p-8",
      lg: "max-w-3xl p-4",
    };
    return mapping[cardSize] ?? mapping.md;
  }, [cardSize]);

  const Container = ({ children }) => (
    <div
      className={
        fullscreen
          ? `fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4 ${className}`
          : `w-full ${className}`
      }
      role={fullscreen ? "alert" : undefined}
      aria-live="polite"
    >
      {children}
    </div>
  );

  return (
    <Container>
      <section
        className={`relative overflow-hidden w-full ${sizeCls} rounded-3xl border border-slate-200 bg-white/95 shadow-2xl`}
      >
        <img
          className="absolute top-10 right-9 w-24 hidden md:block"
          src={logoSrc}
        />
        <motion.div
          className="pointer-events-none absolute inset-0 opacity-60"
          animate={{ scale: [1, 1.05, 1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ repeat: Infinity, duration: 14, repeatType: "reverse" }}
        >
          <div className={`absolute inset-4 rounded-2xl ${palette.ring}`} />
        </motion.div>

        <div className="relative z-10 flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <h2 className="text-lg md:text-2xl font-semibold text-slate-900 flex items-center gap-3">
              <Spinner size="md" />
              {title}
            </h2>
            <p className="text-sm text-slate-600">{subtitle}</p>
          </div>

          <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gray-100 p-5 text-sm text-slate-700">
            <AnimatePresence mode="wait">
              <motion.p
                key={`tip-${tipIndex}`}
                className="flex items-center gap-2 text-sm md:text-base font-semibold"
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -12, opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                {tips[tipIndex]}...
              </motion.p>
            </AnimatePresence>
            <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-500">
              {[0, 1, 2].map((step) => (
                <motion.span
                  key={`pulse-${step}`}
                  className="h-1.5 w-2 md:w-6 rounded-full bg-blue-400"
                  animate={{ scale: [1, 1.3, 1], opacity: [0.4, 1, 0.4] }}
                  transition={{
                    repeat: Infinity,
                    duration: 1.2,
                    delay: step * 0.15,
                  }}
                />
              ))}
              <span>Busca em andamento • aguarde</span>
            </div>
          </div>
        </div>
      </section>
    </Container>
  );
}
